import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ROUTE_CRITICAL_KEYS, invalidateAffected, refreshOnNavigate } from "@/lib/routeRefresh";
import { DataFreshness } from "@/components/ui/DataFreshness";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { StatusBadge } from "@/components/ui/Badge";
import { TransferDialog } from "@/components/accounts/TransferDialog";
import { ReturnToReserveDialog } from "@/components/accounts/ReturnToReserveDialog";
import {
  ReserveMoveDialog,
  type ReserveMoveMode,
} from "@/components/accounts/ReserveMoveDialog";
import { SparkAmount } from "@/components/ui/SparkAmount";
import { CurrencyAmount, CurrencyBadge } from "@/components/ui/CurrencyAmount";
import { ReserveBalances } from "@/components/accounts/ReserveBalances";
import { BrokerAccountLabel } from "@/components/accounts/BrokerAccountLabel";
import { reserveBalanceRows } from "@/lib/reserveBalances";
import { brokerAccountMismatch } from "@/lib/brokerAccount";
import { APIError } from "@/lib/api";
import {
  createMakerAccount,
  fetchLinkedAccount,
  fetchMakerAccounts,
  fetchMyPermissions,
  keys,
  repairReserveCurrencies,
  updateMakerAccountFees,
} from "@/lib/queries";
import { CURRENCY_CODES, parseUnitToMinor, type CurrencyCode } from "@/lib/currency";
import type { MMLinkedAccount, MMMakerAccount } from "@/lib/types";
import { ModeBadge } from "@/components/runs/RunStateTabs";
import { useI18n } from "@/i18n/I18nProvider";
import { strategyNameLabel } from "@/i18n/strategyCopy";

// The search box lives in the URL (SM-564): opening an account and
// coming back through the breadcrumb or the browser restores the
// filtered list the operator left. Optional so <Link to="/accounts">
// stays valid everywhere.
interface AccountsSearch {
  q?: string;
}

export const Route = createFileRoute("/_app/accounts")({
  // SM-563 — re-read this page's data on arrival.
  loader: refreshOnNavigate(ROUTE_CRITICAL_KEYS.accounts),
  validateSearch: (search: Record<string, unknown>): AccountsSearch => ({
    q: typeof search.q === "string" && search.q ? search.q : undefined,
  }),
  component: AccountsPage,
});

function AccountsPage() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate({ from: Route.fullPath });
  const q = Route.useSearch().q ?? "";
  const setQ = (value: string) =>
    navigate({ search: (prev) => ({ ...prev, q: value || undefined }), replace: true });
  const [accountName, setAccountName] = useState("");
  const [codeSuffix, setCodeSuffix] = useState("");
  const [purpose, setPurpose] = useState("");
  const [fundingSpark, setFundingSpark] = useState("10000");
  const [fundingCurrency, setFundingCurrency] = useState<CurrencyCode>("GOLD");
  const [editingFees, setEditingFees] = useState<FeeEditState | null>(null);
  const [transferring, setTransferring] = useState(false);
  const [returning, setReturning] = useState<MMMakerAccount | null>(null);
  const [reserveMove, setReserveMove] = useState<{
    account: MMMakerAccount;
    mode: ReserveMoveMode;
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Shown after a successful create. It exists to correct an easy
  // misreading: linking a SYSTEM_MM child does NOT make its reserve
  // spendable, and an operator who assumes otherwise starts a run
  // against an empty execution wallet.
  const [notice, setNotice] = useState<string | null>(null);
  const accountsQ = useQuery({
    queryKey: keys.accounts(q, 100, 0),
    queryFn: () => fetchMakerAccounts(q, 100, 0),
    // Funding happens in Backoffice Treasury, not here, so this page
    // cannot invalidate on the mutation that changed the numbers. What
    // makes the balances right anyway is now app-wide (SM-563): focus
    // refetching is on for every query, and arriving through the
    // sidebar re-reads this page's data. The local opt-in that used to
    // sit here was the only one in the console.
  });
  const permissionsQ = useQuery({
    queryKey: keys.myPermissions(),
    queryFn: fetchMyPermissions,
    staleTime: 60_000,
    retry: 1,
  });
  const canReturnExecution =
    permissionsQ.data?.permissions.includes("MM_ACCOUNT_RETURN") ?? false;
  const canFundExecution =
    permissionsQ.data?.permissions.includes("SYSTEM_ACCOUNT_FUND_USER") ?? false;
  const canMoveReserveToParent =
    permissionsQ.data?.permissions.includes("SYSTEM_ACCOUNT_TRANSFER") ?? false;
  const create = useMutation({
    mutationFn: createMakerAccount,
    onSuccess: () => {
      setErr(null);
      setNotice(t("accounts.createdNotFunded"));
      setAccountName("");
      setCodeSuffix("");
      setPurpose("");
      invalidateAffected(qc, "accounts");
    },
    onError: (e) => {
      setNotice(null);
      setErr(e instanceof APIError ? e.message : t("accounts.error.create"));
    },
  });
  // Which account's Repair button is spinning. One mutation serves
  // every row, so without this every row's button would show progress
  // for a repair the operator started on one of them.
  const [repairingUser, setRepairingUser] = useState<string | null>(null);
  // Provision the currency wallets a reserve is missing (SM-526).
  // Creates EMPTY wallets and moves no money, so it needs no
  // confirmation — and it is idempotent, so a double-click is safe.
  const repair = useMutation({
    mutationFn: repairReserveCurrencies,
    onSuccess: (res) => {
      setErr(null);
      setRepairingUser(null);
      const provisioned = res.summary.PROVISIONED ?? 0;
      const missing = res.summary.MISSING ?? 0;
      setNotice(
        missing > 0
          ? t("accounts.reserveCurrency.repairedWithFailures", { provisioned, missing })
          : t("accounts.reserveCurrency.repaired", { provisioned }),
      );
      invalidateAffected(qc, "accounts");
    },
    onError: (e) => {
      setNotice(null);
      setRepairingUser(null);
      setErr(e instanceof APIError ? e.message : t("accounts.reserveCurrency.error"));
    },
  });
  const updateFees = useMutation({
    mutationFn: ({ userID, makerFeeRate, takerFeeRate }: { userID: string; makerFeeRate: string; takerFeeRate: string }) =>
      updateMakerAccountFees(userID, {
        maker_fee_rate: makerFeeRate,
        taker_fee_rate: takerFeeRate,
        fee_rate_precision: 6,
        note: "Updated from MM web account page",
      }),
    onSuccess: () => {
      setErr(null);
      setEditingFees(null);
      invalidateAffected(qc, "accounts");
    },
    onError: (e) => setErr(e instanceof APIError ? e.message : t("accounts.error.updateFees")),
  });

  return (
    <div className="space-y-6">
      <header>
        <div className="text-2xs uppercase tracking-widest t-mute">{t("nav.operate")}</div>
        <h1 className="page-title">{t("accounts.title")}</h1>
        <p className="page-subtitle">
          {t("accounts.subtitle")}
        </p>
      </header>
      <DataFreshness query={accountsQ} />

      <Card>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_280px_1fr_180px_auto]">
          <Input label={t("accounts.name")} value={accountName} onChange={(e) => setAccountName(e.target.value)} placeholder={t("accounts.namePlaceholder")} />
          <Input
            label={t("accounts.codeSuffix")}
            value={codeSuffix}
            onChange={(e) => setCodeSuffix(sanitizeCodeSuffix(e.target.value))}
            placeholder="SUMMER_2026"
            hint={codeSuffix ? t("accounts.fullCode", { suffix: codeSuffix }) : t("accounts.codeHint")}
          />
          <Input label={t("accounts.purpose")} value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder={t("accounts.purposePlaceholder")} />
          {/* SM-470: the funding amount now names its currency, and the
              wire conversion goes through the shared parser rather than
              a local re-implementation of the same arithmetic. GOLD is
              the default because it is what every desk was funded in
              before this epic — a create that omits the choice behaves
              exactly as it always did. */}
          <div>
            <Input
              label={t("accounts.initialFunding")}
              type="number"
              min={0}
              step="0.01"
              value={fundingSpark}
              onChange={(e) => setFundingSpark(e.target.value)}
              hint={t("accounts.initialFundingHint", { currency: fundingCurrency })}
            />
            <div className="mt-2 flex gap-1" role="group" aria-label={t("accounts.fundingCurrency")}>
              {CURRENCY_CODES.map((code) => (
                <button
                  key={code}
                  type="button"
                  aria-pressed={code === fundingCurrency}
                  onClick={() => setFundingCurrency(code)}
                  className={
                    code === fundingCurrency
                      ? "rounded-full border border-neon/40 bg-neon/10 px-2 py-0.5 text-xs font-semibold text-neon"
                      : "rounded-full border border-border px-2 py-0.5 text-xs text-muted hover:text-fg"
                  }
                >
                  {code}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-start lg:pt-[22px]">
            <Button
              type="button"
              className="h-10"
              loading={create.isPending}
              disabled={!accountName.trim() || !codeSuffix}
              onClick={() => {
                // One parser for every typed amount in the console
                // (src/lib/currency.ts). The local
                // `Math.round(n * 10 ** (SPARK_MINOR_PRECISION - 2))`
                // this replaces was a second implementation of the same
                // conversion, and a second implementation is a second
                // thing to get wrong when a currency's entry unit
                // differs from GOLD's.
                const minor = parseUnitToMinor(fundingSpark, fundingCurrency);
                create.mutate({
                  account_name: accountName.trim(),
                  code_suffix: codeSuffix,
                  purpose: purpose.trim() || undefined,
                  display_name: accountName.trim(),
                  funding_minor: Number.isFinite(minor) && minor > 0 ? minor : 0,
                });
              }}
            >
              {t("common.create")}
            </Button>
          </div>
        </div>
        {err && <div className="banner warn mt-3">{err}</div>}
        {notice && !err && (
          <div className="banner mt-3" role="status">
            {notice}
          </div>
        )}
      </Card>

      <Card>
        <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h3 className="section-title">{t("accounts.selectable")}</h3>
            <p className="mt-1 text-xs t-mute">{t("accounts.count", { count: accountsQ.data?.page_info.total ?? 0 })}</p>
          </div>
          <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-end">
            <div className="w-full md:w-80">
              <Input label={t("common.search")} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("accounts.searchPlaceholder")} />
            </div>
            <Button
              type="button"
              variant="ghost"
              className="h-10"
              onClick={() => setTransferring(true)}
            >
              {t("accounts.transfer.open")}
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-2xs uppercase tracking-widest text-t-3">
              <tr className="border-b border-line-1">
                <th className="py-2 pr-3 font-medium">{t("common.account")}</th>
                <th className="px-3 py-2 font-medium">{t("common.status")}</th>
                <th className="px-3 py-2 font-medium">{t("accounts.bot")}</th>
                <th className="px-3 py-2 font-medium">{t("accounts.platformFee")}</th>
                {/* Two owners, two columns. They are never added
                    together and never share a "balance" label. */}
                <th className="px-3 py-2 font-medium">{t("accounts.executionAvailable")}</th>
                <th className="px-3 py-2 font-medium">{t("accounts.fundingReserve")}</th>
                <th className="px-3 py-2 font-medium">{t("accounts.userId")}</th>
              </tr>
            </thead>
            <tbody>
              {accountsQ.data?.accounts.map((a) => (
                <tr key={a.user_id} className="border-b border-line-1 last:border-0">
                  <td className="py-3 pr-3">
                    {/* The name opens the account's own page (SM-564);
                        the list's search rides along so the breadcrumb
                        brings the operator back to this filtered view. */}
                    <Link
                      to="/accounts/$id"
                      params={{ id: a.user_id }}
                      search={{ q: q || undefined }}
                      className="font-medium text-t-1 hover:text-accent hover:underline"
                      title={t("accounts.openDetail")}
                    >
                      {a.display_name || t("accounts.unnamed")}
                    </Link>
                    <div className="text-xs t-mute">{a.email}</div>
                  </td>
                  <td className="px-3 py-3 text-xs"><StatusBadge status={a.status} /></td>
                  <td className="px-3 py-3">
                    {a.active_run_id ? (
                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <StatusBadge status={a.active_run_status ?? "PENDING"} />
                          <ModeBadge mode={a.active_trading_mode} />
                        </div>
                        {/* The preset NAME, not an id: this column
                            answers "which bot is on this desk", and an
                            id is the one answer an operator scanning a
                            fleet list cannot read. */}
                        <div className="truncate text-xs text-t-1" title={a.active_preset_name}>
                          {a.active_preset_name || t("accounts.unknownPreset")}
                        </div>
                        <div className="text-xs t-mute">
                          {a.active_strategy
                            ? strategyNameLabel(a.active_strategy, t)
                            : t("accounts.unknownStrategy")}
                        </div>
                        <Link
                          to="/runs/$id"
                          params={{ id: a.active_run_id }}
                          className="mono block text-[10px] text-t-4 hover:t-emph"
                          title={a.active_run_id}
                        >
                          {t("accounts.run", { id: a.active_run_id.slice(0, 14) })}
                        </Link>
                      </div>
                    ) : (
                      <span className="text-xs t-mute">{t("accounts.idle")}</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="space-y-1">
                      <div className="mono text-xs text-t-1">
                        {t("accounts.feeSummary", {
                          maker: formatFeePercent(a.maker_fee_rate, a.fee_rate_precision ?? 6).replace(/%$/, ""),
                          taker: formatFeePercent(a.taker_fee_rate, a.fee_rate_precision ?? 6).replace(/%$/, ""),
                        })}
                      </div>
                      <button
                        type="button"
                        className="text-2xs uppercase tracking-widest text-accent"
                        onClick={() => setEditingFees(feeEditState(a))}
                      >
                        {t("common.edit")}
                      </button>
                    </div>
                  </td>
                  <td className="mono px-3 py-3 text-xs">
                    <ExecutionBalance account={a} />
                    <ReturnExecutionAction
                      account={a}
                      visible={canReturnExecution}
                      onOpen={() => setReturning(a)}
                    />
                  </td>
                  <td className="mono px-3 py-3 text-xs">
                    <FundingReserve account={a} />
                    <ReserveRepairAction
                      account={a}
                      repairing={repair.isPending && repairingUser === a.user_id}
                      onRepair={() => {
                        setRepairingUser(a.user_id);
                        repair.mutate({ user_ids: [a.user_id] });
                      }}
                    />
                    <ReserveActions
                      account={a}
                      canFundExecution={canFundExecution}
                      canMoveToParent={canMoveReserveToParent}
                      onOpen={(mode) => setReserveMove({ account: a, mode })}
                    />
                  </td>
                  <td className="mono max-w-[18rem] px-3 py-3 text-xs text-t-3">
                    <div className="truncate">{a.user_id}</div>
                    {/* SM-786: tells two desks' broker accounts apart;
                        nothing renders while attribution is off. */}
                    <BrokerAccountLabel id={a.broker_account_id} />
                  </td>
                </tr>
              ))}
              {!accountsQ.isLoading && accountsQ.data?.accounts.length === 0 && (
                <tr>
                  <td className="py-8 text-sm t-mute" colSpan={7}>{t("accounts.noMatch")}</td>
                </tr>
              )}
              {accountsQ.isLoading && (
                <tr>
                  <td className="py-8 text-sm t-mute" colSpan={7}>{t("common.loadingAccounts")}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      {transferring && (
        <TransferDialog
          accounts={accountsQ.data?.accounts ?? []}
          onClose={() => setTransferring(false)}
          onTransferred={() => {
            // Refetch BOTH sides from the server rather than patching
            // the two rows from the response: a run may have filled an
            // order in the meantime, and a locally-patched balance
            // would show a figure that was never true.
            setErr(null);
            setNotice(t("accounts.transfer.done"));
            invalidateAffected(qc, "accounts");
          }}
        />
      )}
      {returning && (
        <ReturnToReserveDialog
          account={returning}
          onClose={() => setReturning(null)}
          onReturned={() => {
            setErr(null);
            setNotice(t("accounts.return.done"));
            invalidateAffected(qc, "accounts");
          }}
        />
      )}
      {reserveMove && (
        <ReserveMoveDialog
          account={reserveMove.account}
          mode={reserveMove.mode}
          onClose={() => setReserveMove(null)}
          onMoved={() => {
            setErr(null);
            setNotice(
              reserveMove.mode === "fund"
                ? t("accounts.reserve.fundDone")
                : t("accounts.reserve.parentDone"),
            );
            invalidateAffected(qc, "accounts");
          }}
        />
      )}
      {editingFees && (
        <div className="modal-backdrop">
          <div className="modal-panel max-w-md">
            <div className="mb-4">
              <h3 className="section-title">{t("accounts.editFee")}</h3>
              <p className="mt-1 text-xs t-mute">{editingFees.email || editingFees.userID}</p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                label={t("accounts.makerFee")}
                inputMode="decimal"
                value={editingFees.makerPercent}
                onChange={(e) => setEditingFees({ ...editingFees, makerPercent: e.target.value })}
                placeholder="0"
              />
              <Input
                label={t("accounts.takerFee")}
                inputMode="decimal"
                value={editingFees.takerPercent}
                onChange={(e) => setEditingFees({ ...editingFees, takerPercent: e.target.value })}
                placeholder="0"
              />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setEditingFees(null)}>{t("common.cancel")}</Button>
              <Button
                type="button"
                loading={updateFees.isPending}
                onClick={() => updateFees.mutate({
                  userID: editingFees.userID,
                  makerFeeRate: percentToMinor(editingFees.makerPercent, 6),
                  takerFeeRate: percentToMinor(editingFees.takerPercent, 6),
                })}
              >
                {t("common.save")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Execution balance — the money this account can actually trade with.
 *
 * `undefined` renders as "Unavailable", NOT as an em-dash or a zero: an
 * operator deciding whether to fund a desk must be able to tell "the
 * wallet is empty" from "we couldn't read the wallet".
 *
 * Reads execution_available_minor, falling back to the deprecated
 * available_minor so responses from a server that predates SM-342 still
 * render during the rollout.
 */
function ExecutionBalance({ account }: { account: MMMakerAccount }) {
  const { t } = useI18n();
  const available = account.execution_available_minor ?? account.available_minor;
  const reserved = account.execution_reserved_minor;

  // The per-currency view (SM-470/SM-471). `GET /admin/mm/accounts`
  // carries only the legacy GOLD-shaped fields, so RED and GREEN come
  // from the linked-account read — one per linked desk, and only for
  // desks that HAVE a link.
  const linked = useQuery({
    queryKey: keys.linkedAccount(account.funding_account_code ?? ""),
    queryFn: () => fetchLinkedAccount(account.funding_account_code ?? ""),
    enabled: !!account.funding_account_code,
  });

  if (available === undefined && !linked.data) {
    return <><DataFreshness query={linked} /><Unavailable /></>;
  }

  const rows = perCurrencyRows(account, linked.data);

  return (
    <div className="space-y-1">
      <DataFreshness query={linked} />
      {rows.map((row) => (
        <div key={row.currency} className="space-y-0.5">
          {/* Never a sum: three currencies, three lines, each labelled.
              A desk's RED and its GOLD have no common total, and one
              combined figure would overstate what it can trade with in
              either (G8). */}
          {row.available === undefined ? (
            <div className="flex items-center gap-1">
              <CurrencyBadge currency={row.currency} />
              <Unavailable />
            </div>
          ) : (
            <CurrencyAmount currency={row.currency} value={row.available} />
          )}
          {row.reserved !== undefined && row.reserved > 0 && (
            <div className="text-[10px] text-t-4" title={t("accounts.executionReservedHint")}>
              {t("accounts.executionReserved")}{" "}
              <CurrencyAmount currency={row.currency} value={row.reserved} />
            </div>
          )}
        </div>
      ))}
      {rows.length === 0 && available !== undefined && (
        <SparkAmount value={available} />
      )}
      {rows.length === 0 && reserved !== undefined && reserved > 0 && (
        <div className="text-[10px] text-t-4" title={t("accounts.executionReservedHint")}>
          {t("accounts.executionReserved")} <SparkAmount value={reserved} />
        </div>
      )}
    </div>
  );
}

export interface CurrencyBalanceRow {
  currency: string;
  /** undefined = unreadable. NEVER coalesced to 0 (SM-342 convention). */
  available?: number;
  reserved?: number;
}

/**
 * One row per currency this desk holds a wallet in.
 *
 * A row is emitted only for a currency the server actually reported: a
 * desk with no RED wallet yet gets no RED row, rather than a "RED 0.00"
 * that would read as an empty wallet rather than as an absent one. When
 * nothing is linked there is nothing per-currency to say, and the
 * caller falls back to the legacy GOLD figure — which is exactly what
 * this console showed before the epic.
 */
export function perCurrencyRows(
  account: MMMakerAccount,
  linked: MMLinkedAccount | undefined,
): CurrencyBalanceRow[] {
  if (!linked?.balances?.length) return [];
  // SM-786: a linked read the server made for a DIFFERENT broker account
  // than this row names is not this desk's money. Keep its currencies
  // (so a RED wallet does not silently vanish) but read every figure as
  // unavailable — never another account's number under this row.
  const foreign = brokerAccountMismatch(account.broker_account_id, linked.broker_account_id);
  // Registry order (RED, GREEN, GOLD) so every desk reads the same way.
  const order = new Map(CURRENCY_CODES.map((c, i) => [c as string, i]));
  return [...linked.balances]
    .filter((b) => order.has(b.currency))
    .sort((a, b) => (order.get(a.currency) ?? 0) - (order.get(b.currency) ?? 0))
    .map((b) => ({
      currency: b.currency,
      available: foreign ? undefined : b.execution_available_minor,
      reserved: foreign ? undefined : b.execution_reserved_minor,
    }));
}

function ReturnExecutionAction({
  account,
  visible,
  onOpen,
}: {
  account: MMMakerAccount;
  visible: boolean;
  onOpen: () => void;
}) {
  const { t } = useI18n();
  if (!visible) return null;

  const available = account.execution_available_minor ?? account.available_minor;
  const linkReady =
    account.funding_link_status === "LINKED" && !!account.funding_account_code;
  const enabled = linkReady && available !== undefined && available > 0;
  const hint = !linkReady
    ? t("accounts.return.linkUnavailable")
    : available === undefined
      ? t("accounts.return.balanceUnavailable")
      : available <= 0
        ? t("accounts.return.empty")
        : t("accounts.return.openHint");

  return (
    <button
      type="button"
      className="mt-1 block text-left text-[10px] uppercase tracking-widest text-accent disabled:cursor-not-allowed disabled:text-t-4"
      disabled={!enabled}
      title={hint}
      onClick={onOpen}
    >
      {t("accounts.return.open")}
    </button>
  );
}

/**
 * Funding reserve — the linked SYSTEM_MM child, one row per currency.
 *
 * Deliberately names the account code once above the rows: the numbers
 * belong to a different wallet family than the execution column next to
 * it, and naming the source is what stops the two reading as one
 * balance. Unlinked and invalid states render as status text with a
 * remediation hint rather than a number, so there is nothing to mistake
 * for money.
 *
 * SM-570 replaced a single unlabelled amount (always GOLD's) plus a row
 * of bare currency pills. The amount and the currency now arrive
 * together on every line, and the pills — which looked like controls
 * but only reported wallet existence — are gone. Choosing a currency
 * still happens in the fund and move dialogs, where the choice changes
 * what the button does.
 */
function FundingReserve({ account }: { account: MMMakerAccount }) {
  const { t } = useI18n();
  const status = account.funding_link_status;

  if (status === "UNLINKED") {
    return <LinkProblem label={t("accounts.linkUnlinked")} hint={t("accounts.linkUnlinkedHint")} />;
  }
  if (status === "INVALID") {
    return <LinkProblem label={t("accounts.linkInvalid")} hint={t("accounts.linkInvalidHint")} />;
  }
  const rows = reserveBalanceRows(account);
  if (rows.length === 0) {
    // Either UNAVAILABLE, or a pre-SM-342 response with no link data at
    // all. Both mean "no reserve figure to show", never "zero".
    return <Unavailable />;
  }
  return (
    <div className="space-y-1" title={t("accounts.fundingReserveHint")}>
      {account.funding_account_code && (
        <div className="text-[10px] text-t-4">{account.funding_account_code}</div>
      )}
      <ReserveBalances rows={rows} />
    </div>
  );
}

/**
 * Repair for the wallets this reserve does NOT hold (SM-526).
 *
 * "The reserve has money in it" and "the reserve can be used for a
 * GAME run" are different facts, and only the first one was ever on
 * this page. An operator looking at a healthy GOLD balance had no way
 * to know the desk had no RED wallet at all — they found out from a run
 * that failed a second after starting.
 *
 * The currency pills this used to draw are gone (SM-570): each
 * currency's own row in the cell above already says whether its wallet
 * exists, and says it with the balance beside it. What is left here is
 * the one thing that was genuinely a control.
 *
 * ABSENT READINESS RENDERS NOTHING. The server omits it for an
 * unlinked account and when the ledger could not be read, and no
 * action is the honest rendering of "not known": offering Repair there
 * would have the operator fixing a reserve that is fine.
 */
function ReserveRepairAction({
  account,
  repairing,
  onRepair,
}: {
  account: MMMakerAccount;
  repairing: boolean;
  onRepair: () => void;
}) {
  const { t } = useI18n();
  const currencies = account.reserve_currencies;
  if (!currencies || currencies.length === 0) return null;
  const missing = currencies.filter((c) => !c.provisioned);
  if (missing.length === 0) return null;
  return (
    <button
      type="button"
      className="mt-1 block text-left text-[10px] uppercase tracking-widest text-accent disabled:cursor-not-allowed disabled:text-t-4"
      disabled={repairing}
      title={t("accounts.reserveCurrency.repairHint", {
        currencies: missing.map((c) => c.currency).join(", "),
      })}
      onClick={onRepair}
    >
      {repairing ? t("accounts.reserveCurrency.repairing") : t("accounts.reserveCurrency.repair")}
    </button>
  );
}

function ReserveActions({
  account,
  canFundExecution,
  canMoveToParent,
  onOpen,
}: {
  account: MMMakerAccount;
  canFundExecution: boolean;
  canMoveToParent: boolean;
  onOpen: (mode: ReserveMoveMode) => void;
}) {
  const { t } = useI18n();
  if (!canFundExecution && !canMoveToParent) return null;

  const available = account.funding_reserve_available_minor;
  const linkReady =
    account.funding_link_status === "LINKED" && !!account.funding_account_code;
  const enabled = linkReady && available !== undefined && available > 0;
  const hint = !linkReady
    ? t("accounts.reserve.linkUnavailable")
    : available === undefined
      ? t("accounts.reserve.balanceUnavailable")
      : available <= 0
        ? t("accounts.reserve.empty")
        : undefined;

  return (
    <div className="mt-1 flex flex-col items-start gap-0.5">
      {canFundExecution && (
        <button
          type="button"
          className="text-left text-[10px] uppercase tracking-widest text-accent disabled:cursor-not-allowed disabled:text-t-4"
          disabled={!enabled}
          title={hint ?? t("accounts.reserve.fundOpenHint")}
          onClick={() => onOpen("fund")}
        >
          {t("accounts.reserve.fundOpen")}
        </button>
      )}
      {canMoveToParent && (
        <button
          type="button"
          className="text-left text-[10px] uppercase tracking-widest text-accent disabled:cursor-not-allowed disabled:text-t-4"
          disabled={!enabled}
          title={hint ?? t("accounts.reserve.parentOpenHint")}
          onClick={() => onOpen("parent")}
        >
          {t("accounts.reserve.parentOpen")}
        </button>
      )}
    </div>
  );
}

function LinkProblem({ label, hint }: { label: string; hint: string }) {
  return (
    <span className="text-xs text-warning" title={hint}>
      {label}
    </span>
  );
}

function Unavailable() {
  const { t } = useI18n();
  return (
    <span className="text-xs t-mute" title={t("accounts.balanceUnavailableHint")}>
      {t("accounts.balanceUnavailable")}
    </span>
  );
}

interface FeeEditState {
  userID: string;
  email: string;
  makerPercent: string;
  takerPercent: string;
}

function feeEditState(a: MMMakerAccount): FeeEditState {
  const precision = a.fee_rate_precision ?? 6;
  return {
    userID: a.user_id,
    email: a.email,
    makerPercent: minorToPercent(a.maker_fee_rate ?? "0", precision) || "0",
    takerPercent: minorToPercent(a.taker_fee_rate ?? "0", precision) || "0",
  };
}

function formatFeePercent(minor: string | undefined, precision: number): string {
  const percent = minorToPercent(minor ?? "0", precision) || "0";
  return `${percent}%`;
}

function minorToPercent(minor: string, precision: number): string {
  if (!minor || minor === "0") return "";
  const shift = Math.max(precision - 2, 0);
  const negative = minor.startsWith("-");
  const digits = (negative ? minor.slice(1) : minor).replace(/^0+/, "") || "0";
  const padded = digits.padStart(shift + 1, "0");
  const intPart = padded.slice(0, padded.length - shift) || "0";
  const fracPart = padded.slice(padded.length - shift).replace(/0+$/, "");
  const body = fracPart ? `${intPart}.${fracPart}` : intPart;
  return negative ? `-${body}` : body;
}

function percentToMinor(input: string, precision: number): string {
  const trimmed = input.trim();
  if (!trimmed || trimmed === "-") return "0";
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return "0";
  const shift = Math.max(precision - 2, 0);
  const negative = trimmed.startsWith("-");
  const body = negative ? trimmed.slice(1) : trimmed;
  const [intPart, fracPartRaw = ""] = body.split(".");
  const fracPart = (fracPartRaw + "0".repeat(shift)).slice(0, shift);
  const combined = (intPart + fracPart).replace(/^0+(?=\d)/, "") || "0";
  return negative ? `-${combined}` : combined;
}

function sanitizeCodeSuffix(s: string): string {
  return s.toUpperCase().replace(/[^A-Z0-9_.-]/g, "").slice(0, 40);
}
